import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  deleteDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import {
  FiUsers,
  FiTrendingUp,
  FiClock,
  FiDatabase,
  FiRefreshCw,
  FiSend,
  FiActivity,
  FiShield,
  FiAlertTriangle,
  FiUpload,
  FiPlusCircle,
  FiX,
  FiFileText,
  FiUser,
  FiMail,
  FiPhone,
  FiGlobe
} from 'react-icons/fi';

import Header from '../Header';
import { auth, database } from '../../firebaseConfig';
import { formatFsTimestamp, isAdminUser } from '../../utils/admin';

const initialStats = {
  totalUsers: 0,
  engagedUsers: 0,
  totalAttempts: 0,
  avgAttempts: 0,
  newUsersWeek: 0,
  totalQuestions: 0,
  totalVisits: 0,
};

const defaultMcqForm = {
  question: '',
  category: '',
  difficulty: 'medium',
  type: 'multiple',
  correct: '',
  incorrect1: '',
  incorrect2: '',
  incorrect3: '',
  solution: '',
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const [stats, setStats] = useState(initialStats);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [difficultyBreakdown, setDifficultyBreakdown] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [newNotification, setNewNotification] = useState({
    message: '',
    type: 'info',
    priority: 'normal',
    cta: ''
  });

  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [mcqForm, setMcqForm] = useState(defaultMcqForm);
  const [questionSaving, setQuestionSaving] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const formatNumber = (value) =>
    Number.isFinite(value) ? value.toLocaleString() : value;

  useEffect(() => {
    const verifyAccess = async () => {
      if (authLoading) return;
      if (!user) {
        setHasAccess(false);
        setCheckingAccess(false);
        return;
      }

      try {
        const userRef = doc(database, 'users', user.uid);
        const snapshot = await getDoc(userRef);
        const data = snapshot.exists() ? snapshot.data() : {};
        setHasAccess(isAdminUser(user, data));
      } catch (error) {
        console.error('Failed to verify admin role:', error);
        setHasAccess(false);
        setStatus({ type: 'error', message: 'Unable to verify admin privileges.' });
      } finally {
        setCheckingAccess(false);
      }
    };

    verifyAccess();
  }, [user, authLoading]);

  const fetchStats = useCallback(async () => {
    setRefreshing(true);
    setStatus(null);
    try {
      const metricsRef = doc(database, 'metrics', 'global');
      const [userSnapshot, questionSnapshot, metricsSnapshot] = await Promise.all([
        getDocs(collection(database, 'users')),
        getDocs(collection(database, 'questions')),
        getDoc(metricsRef),
      ]);

      const userDocs = userSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const questionDocs = questionSnapshot.docs.map((docSnap) => docSnap.data());
      const metricsData = metricsSnapshot.exists() ? metricsSnapshot.data() : {};

      const totalUsers = userDocs.length;
      const totalAttempts = userDocs.reduce((sum, entry) => {
        const attempts = entry.attempted_questions || entry.attemptedMCQs || [];
        return sum + attempts.length;
      }, 0);
      const engagedUsers = userDocs.filter((entry) => {
        const attempts = entry.attempted_questions || entry.attemptedMCQs || [];
        return attempts.length > 0;
      }).length;

      const avgAttempts = totalUsers ? Math.round(totalAttempts / totalUsers) : 0;
      const now = new Date();
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);

      const newUsersWeek = userDocs.filter((entry) => {
        const createdAt = formatFsTimestamp(entry.createdAt);
        return createdAt && createdAt >= weekAgo;
      }).length;

      const recentUsersList = userDocs
        .map((entry) => {
          const lastSeen =
            formatFsTimestamp(entry.lastActiveAt) ||
            formatFsTimestamp(entry.updatedAt) ||
            formatFsTimestamp(entry.createdAt);
          const attempts = entry.attempted_questions || entry.attemptedMCQs || [];
          return {
            id: entry.id,
            email: entry.email,
            name: entry.personalInfo?.name,
            country: entry.personalInfo?.country,
            preparingFor: entry.personalInfo?.preparingFor,
            attempts: attempts.length,
            lastActiveAt: lastSeen,
            raw: entry,
          };
        })
        .sort((a, b) => {
          const aTime = a.lastActiveAt ? a.lastActiveAt.getTime() : 0;
          const bTime = b.lastActiveAt ? b.lastActiveAt.getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 6);

      const totalQuestions = questionDocs.length;
      const difficultyMap = {};
      const categoryMap = {};

      questionDocs.forEach((question) => {
        const difficulty = (question.difficulty || 'unspecified').toLowerCase();
        const category = question.category || 'Other';

        difficultyMap[difficulty] = (difficultyMap[difficulty] || 0) + 1;
        categoryMap[category] = (categoryMap[category] || 0) + 1;
      });

      const categoryData = Object.entries(categoryMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, value]) => ({
          label,
          value,
          percent: totalQuestions ? Math.round((value / totalQuestions) * 100) : 0,
        }));

      const difficultyData = Object.entries(difficultyMap).map(([label, value]) => ({
        label,
        value,
        percent: totalQuestions ? Math.round((value / totalQuestions) * 100) : 0,
      }));

      setStats({
        totalUsers,
        engagedUsers,
        totalAttempts,
        avgAttempts,
        newUsersWeek,
        totalQuestions,
        totalVisits: metricsData.totalVisits || 0,
      });
      setCategoryBreakdown(categoryData);
      setDifficultyBreakdown(difficultyData);
      setRecentUsers(recentUsersList);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      setStatus({ type: 'error', message: 'Unable to load site statistics.' });
    } finally {
      setRefreshing(false);
    }
  }, [database]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchStats();
  }, [hasAccess, fetchStats]);

  const buildQuestionPayload = (source) => {
    const questionText = source.question?.trim();
    const correctAnswer =
      source.correct_answer?.trim() ||
      source.correctAnswer?.trim() ||
      source.correct?.trim();

    let incorrectPool = [];
    if (Array.isArray(source.incorrect_answers)) incorrectPool = source.incorrect_answers;
    else if (Array.isArray(source.incorrectAnswers)) incorrectPool = source.incorrectAnswers;
    else if (Array.isArray(source.incorrects)) incorrectPool = source.incorrects;

    incorrectPool = [
      ...incorrectPool,
      source.incorrect1,
      source.incorrect2,
      source.incorrect3,
    ].filter((entry) => typeof entry === 'string' && entry.trim());

    if (Array.isArray(source.options) && incorrectPool.length === 0 && correctAnswer) {
      incorrectPool = source.options.filter((option) => option !== correctAnswer);
    }

    const uniqueIncorrects = Array.from(
      new Set(
        incorrectPool
          .map((entry) => entry.trim())
          .filter((value) => value && value !== correctAnswer)
      )
    );

    if (!questionText || !correctAnswer || uniqueIncorrects.length < 1) {
      throw new Error('Question, correct answer, and at least one incorrect answer are required.');
    }

    return {
      question: questionText,
      correct_answer: correctAnswer,
      incorrect_answers: uniqueIncorrects,
      category: source.category?.trim() || 'General Knowledge',
      difficulty: (source.difficulty || 'medium').toLowerCase(),
      type: source.type || 'multiple',
      solution: source.solution || source.explanation || '',
      createdAt: serverTimestamp(),
      createdBy: user?.email || 'admin',
    };
  };

  const handleCreateQuestion = async (event) => {
    event.preventDefault();
    setQuestionSaving(true);
    setStatus(null);
    try {
      const payload = buildQuestionPayload({
        ...mcqForm,
        incorrect_answers: [mcqForm.incorrect1, mcqForm.incorrect2, mcqForm.incorrect3],
        correct_answer: mcqForm.correct,
      });

      await addDoc(collection(database, 'questions'), payload);
      setMcqForm(defaultMcqForm);
      setStatus({ type: 'success', message: 'Question added to the question bank.' });
      fetchStats();
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save MCQ.' });
    } finally {
      setQuestionSaving(false);
    }
  };

  const handleJsonUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBulkUploading(true);
    setBulkSummary(null);
    setStatus(null);

    try {
      const fileContents = await file.text();
      const parsed = JSON.parse(fileContents);
      const items = Array.isArray(parsed) ? parsed : parsed.questions;

      if (!Array.isArray(items) || items.length === 0) {
        throw new Error('JSON must contain an array of questions.');
      }

      let successCount = 0;
      let failureCount = 0;

      for (const item of items) {
        try {
          const payload = buildQuestionPayload(item);
          await addDoc(collection(database, 'questions'), payload);
          successCount += 1;
        } catch (error) {
          failureCount += 1;
          console.error('Skipping invalid question:', error);
        }
      }

      setBulkSummary({
        fileName: file.name,
        successCount,
        failureCount,
      });

      setStatus({
        type: failureCount ? 'error' : 'success',
        message: failureCount
          ? `Imported ${successCount} questions. ${failureCount} entries failed validation.`
          : `Imported ${successCount} questions successfully.`,
      });

      fetchStats();
    } catch (error) {
      console.error('Bulk upload failed:', error);
      setStatus({ type: 'error', message: error.message || 'Invalid JSON file.' });
      setBulkSummary(null);
    } finally {
      setBulkUploading(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    if (!hasAccess) return;
    const notificationsQuery = query(
      collection(database, 'notifications'),
      orderBy('timestamp', 'desc'),
      limit(12)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const items = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setNotifications(items);
    });

    return () => unsubscribe();
  }, [hasAccess]);

  const handleSendNotification = async (event) => {
    event.preventDefault();
    if (!newNotification.message.trim()) {
      setStatus({ type: 'error', message: 'Notification message is required.' });
      return;
    }

    setActionLoading(true);
    setStatus(null);
    try {
      await addDoc(collection(database, 'notifications'), {
        message: newNotification.message.trim(),
        type: newNotification.type,
        priority: newNotification.priority,
        cta: newNotification.cta?.trim() || null,
        timestamp: serverTimestamp(),
        createdBy: user?.email || 'admin',
      });
      setNewNotification({
        message: '',
        type: 'info',
        priority: 'normal',
        cta: ''
      });
      setStatus({ type: 'success', message: 'Notification broadcasted successfully.' });
    } catch (error) {
      console.error('Failed to push notification:', error);
      setStatus({ type: 'error', message: 'Notification failed. Try again later.' });
    } finally {
      setActionLoading(false);
    }
  };

  const archiveOldNotifications = async () => {
    setActionLoading(true);
    setStatus(null);
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 30);
      const cutoff = Timestamp.fromDate(cutoffDate);

      const archiveQuery = query(
        collection(database, 'notifications'),
        where('timestamp', '<', cutoff)
      );

      const snapshot = await getDocs(archiveQuery);
      const deletions = snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));
      await Promise.all(deletions);

      setStatus({ type: 'success', message: 'Archived notifications older than 30 days.' });
    } catch (error) {
      console.error('Failed to archive notifications:', error);
      setStatus({ type: 'error', message: 'Could not archive old notifications.' });
    } finally {
      setActionLoading(false);
    }
  };

  const healthIndicators = useMemo(
    () => [
      {
        label: 'Engagement rate',
        value: stats.totalUsers
          ? `${Math.round((stats.engagedUsers / stats.totalUsers) * 100)}%`
          : '0%',
        helper: 'Learners with at least one session',
      },
      {
        label: 'Question coverage',
        value: stats.totalQuestions ? `${categoryBreakdown.length} top subjects` : 'No data',
        helper: 'Based on available question bank',
      },
      {
        label: 'Avg MCQs per learner',
        value: stats.avgAttempts,
        helper: 'Higher is better',
      },
    ],
    [stats, categoryBreakdown]
  );

  const renderStatus = () => {
    if (!status) return null;
    const base =
      status.type === 'success'
        ? 'bg-emerald-500/10 border border-emerald-400 text-emerald-200'
        : 'bg-rose-500/10 border border-rose-400 text-rose-100';

    return (
      <div className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${base}`}>
        <FiAlertTriangle className="text-lg" />
        <span>{status.message}</span>
      </div>
    );
  };

  const renderUserDrawer = () => {
    if (!selectedUser) return null;
    const data = selectedUser.raw || {};
    const createdAt = formatFsTimestamp(data.createdAt);
    const lastActive =
      selectedUser.lastActiveAt ||
      formatFsTimestamp(data.lastActiveAt) ||
      formatFsTimestamp(data.updatedAt);
    const totalAttempts =
      (data.attempted_questions || data.attemptedMCQs || []).length;
    const devices = Array.isArray(data.devices) ? data.devices : [];

    return (
      <div
        className="fixed inset-0 z-50 bg-black/70 flex justify-end"
        onClick={() => setSelectedUser(null)}
      >
        <div
          className="w-full max-w-lg h-full bg-slate-900 border-l border-slate-800 shadow-2xl overflow-y-auto p-6"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
                Learner Profile
              </p>
              <h3 className="text-2xl font-semibold mt-1">
                {selectedUser.name || selectedUser.email}
              </h3>
              <p className="text-slate-400 text-sm">{selectedUser.email}</p>
            </div>
            <button
              onClick={() => setSelectedUser(null)}
              className="p-2 rounded-full bg-slate-800 hover:bg-slate-700 transition"
            >
              <FiX />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-800/60 rounded-xl p-4">
              <p className="text-xs text-slate-400">Total attempts</p>
              <p className="text-2xl font-semibold mt-1">{totalAttempts}</p>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4">
              <p className="text-xs text-slate-400">Last active</p>
              <p className="text-sm mt-1">
                {lastActive ? lastActive.toLocaleString() : 'No activity yet'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-800 p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <FiUser /> Personal details
              </h4>
              <dl className="space-y-2 text-sm text-slate-400">
                <div className="flex justify-between">
                  <dt>Name</dt>
                  <dd className="text-white">{data.personalInfo?.name || '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Email</dt>
                  <dd className="flex items-center gap-2 text-white">
                    <FiMail /> {data.email || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Phone</dt>
                  <dd className="flex items-center gap-2 text-white">
                    <FiPhone /> {data.personalInfo?.phoneNumber || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Country</dt>
                  <dd className="flex items-center gap-2 text-white capitalize">
                    <FiGlobe /> {data.personalInfo?.country || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Preparing for</dt>
                  <dd className="text-white">
                    {data.personalInfo?.preparingFor || '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Joined</dt>
                  <dd className="text-white">
                    {createdAt ? createdAt.toLocaleString() : '—'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-xl border border-slate-800 p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <FiFileText /> Devices
              </h4>
              {devices.length === 0 ? (
                <p className="text-sm text-slate-500">No devices registered yet.</p>
              ) : (
                <ul className="space-y-2 text-sm text-slate-300 list-disc list-inside">
                  {devices.map((device, index) => (
                    <li key={`${device}-${index}`} className="break-words">
                      {device}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (checkingAccess || authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-200">
        <div className="animate-pulse text-center">
          <FiShield className="mx-auto text-3xl mb-3" />
          <p className="text-lg">Verifying admin access…</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-center px-4">
        <div className="max-w-md space-y-4">
          <FiShield className="mx-auto text-4xl text-rose-400" />
          <h1 className="text-2xl font-semibold text-white">Admin access required</h1>
          <p className="text-slate-400">
            You need an administrator role to view operational data. Please contact the Prepify
            team if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.25em] text-slate-500">Control Center</p>
            <h1 className="text-3xl font-semibold mt-1">Admin Operations</h1>
            <p className="text-slate-400 text-sm">
              Signed in as {user?.email}. Monitor health metrics and orchestrate broadcasts.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-500 transition"
            >
              Exit admin
            </button>
            <button
              onClick={fetchStats}
              disabled={refreshing}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 flex items-center gap-2 transition"
            >
              <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
              Refresh data
            </button>
          </div>
        </div>

        {renderStatus()}

        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Registered learners</span>
              <FiUsers className="text-indigo-400" />
            </div>
            <p className="text-3xl font-semibold mt-3">{formatNumber(stats.totalUsers)}</p>
            <p className="text-xs text-slate-500 mt-1">+{stats.newUsersWeek} joined this week</p>
          </article>

          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">MCQs attempted</span>
              <FiTrendingUp className="text-emerald-400" />
            </div>
            <p className="text-3xl font-semibold mt-3">{formatNumber(stats.totalAttempts)}</p>
            <p className="text-xs text-slate-500 mt-1">
              {stats.avgAttempts} avg. per learner
            </p>
          </article>

          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Engaged users</span>
              <FiClock className="text-amber-400" />
            </div>
            <p className="text-3xl font-semibold mt-3">{formatNumber(stats.engagedUsers)}</p>
            <p className="text-xs text-slate-500 mt-1">Active in the current release</p>
          </article>

          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Question bank</span>
              <FiDatabase className="text-sky-400" />
            </div>
            <p className="text-3xl font-semibold mt-3">{formatNumber(stats.totalQuestions)}</p>
            <p className="text-xs text-slate-500 mt-1">Across tracked categories</p>
          </article>

          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Site visits</span>
              <FiGlobe className="text-cyan-400" />
            </div>
            <p className="text-3xl font-semibold mt-3">{formatNumber(stats.totalVisits)}</p>
            <p className="text-xs text-slate-500 mt-1">All-time tracked sessions</p>
          </article>
        </section>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Top subjects (5)</h2>
                <span className="text-slate-500 text-sm">
                  Based on {formatNumber(stats.totalQuestions)} questions
                </span>
              </div>
              <div className="space-y-4">
                {categoryBreakdown.length === 0 && (
                  <p className="text-sm text-slate-500">No category data detected.</p>
                )}
                {categoryBreakdown.map((category) => (
                  <div key={category.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{category.label}</span>
                      <span className="text-slate-400">
                        {category.value} • {category.percent}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400"
                        style={{ width: `${category.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Recent learners</h2>
                <span className="text-slate-500 text-sm">Last 6 active profiles</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-slate-400 border-b border-slate-800">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Learner</th>
                      <th className="py-2 pr-4 font-medium">Attempts</th>
                      <th className="py-2 font-medium">Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsers.length === 0 && (
                      <tr>
                        <td colSpan="3" className="py-4 text-center text-slate-500">
                          No activity detected yet.
                        </td>
                      </tr>
                    )}
                    {recentUsers.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-b border-slate-800/70 cursor-pointer hover:bg-slate-800/40"
                        onClick={() => setSelectedUser(entry)}
                      >
                        <td className="py-3 pr-4">
                          <p className="font-medium text-white">
                            {entry.name || entry.email}
                          </p>
                          <p className="text-xs text-slate-500">{entry.email}</p>
                        </td>
                        <td className="py-3 pr-4">{entry.attempts}</td>
                        <td className="py-3 text-slate-400">
                          {entry.lastActiveAt
                            ? entry.lastActiveAt.toLocaleString()
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FiActivity /> Site health
              </h2>
              <div className="space-y-4">
                {healthIndicators.map((indicator) => (
                  <div key={indicator.label} className="bg-slate-900/60 rounded-xl p-4">
                    <p className="text-sm text-slate-400">{indicator.label}</p>
                    <p className="text-2xl font-semibold mt-1">{indicator.value}</p>
                    <p className="text-xs text-slate-500">{indicator.helper}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h2 className="text-lg font-semibold mb-4">Difficulty mix</h2>
              <div className="flex flex-wrap gap-3">
                {difficultyBreakdown.length === 0 && (
                  <p className="text-sm text-slate-500">No difficulty data yet.</p>
                )}
                {difficultyBreakdown.map((item) => (
                  <div
                    key={item.label}
                    className="px-3 py-2 rounded-xl bg-slate-800/80 text-sm border border-slate-700"
                  >
                    <span className="font-medium capitalize">{item.label}</span>
                    <span className="text-slate-400 ml-2">
                      {item.value} ({item.percent}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Create MCQ</h2>
              <FiPlusCircle className="text-indigo-400" />
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Craft a single question with full metadata. Each MCQ is saved straight into Firestore.
            </p>
            <form className="space-y-4" onSubmit={handleCreateQuestion}>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Question</label>
                <textarea
                  className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                  rows="3"
                  value={mcqForm.question}
                  onChange={(e) => setMcqForm((prev) => ({ ...prev, question: e.target.value }))}
                  placeholder="Type the full prompt"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Category</label>
                  <input
                    type="text"
                    className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                    value={mcqForm.category}
                    onChange={(e) => setMcqForm((prev) => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g. Mathematics"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Difficulty</label>
                  <select
                    className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                    value={mcqForm.difficulty}
                    onChange={(e) =>
                      setMcqForm((prev) => ({ ...prev, difficulty: e.target.value }))
                    }
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Question type</label>
                  <select
                    className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                    value={mcqForm.type}
                    onChange={(e) => setMcqForm((prev) => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="multiple">Multiple choice</option>
                    <option value="boolean">True / False</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Correct answer</label>
                  <input
                    type="text"
                    className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                    value={mcqForm.correct}
                    onChange={(e) => setMcqForm((prev) => ({ ...prev, correct: e.target.value }))}
                    placeholder="Single correct response"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['incorrect1', 'incorrect2', 'incorrect3'].map((field, index) => (
                  <div key={field}>
                    <label className="text-sm text-slate-400 block mb-1">
                      Incorrect option {index + 1}
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                      value={mcqForm[field]}
                      onChange={(e) =>
                        setMcqForm((prev) => ({ ...prev, [field]: e.target.value }))
                      }
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Solution / explanation</label>
                <textarea
                  className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                  rows="3"
                  value={mcqForm.solution}
                  onChange={(e) => setMcqForm((prev) => ({ ...prev, solution: e.target.value }))}
                  placeholder="Optional supporting text"
                />
              </div>

              <button
                type="submit"
                disabled={questionSaving}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 py-3 font-medium transition disabled:opacity-50"
              >
                <FiPlusCircle />
                {questionSaving ? 'Saving…' : 'Publish question'}
              </button>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Bulk upload (JSON)</h2>
              <FiUpload className="text-indigo-400" />
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Import a set of MCQs using the same schema we store in Firestore. Perfect for
              migrating curated question banks.
            </p>

            <label className="flex items-center justify-center gap-2 w-full rounded-xl border border-dashed border-slate-700 bg-slate-950/30 py-6 text-slate-300 cursor-pointer hover:border-indigo-500 transition">
              <FiUpload className="text-xl" />
              <span>{bulkUploading ? 'Uploading…' : 'Select JSON file'}</span>
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleJsonUpload}
                disabled={bulkUploading}
              />
            </label>

            {bulkSummary && (
              <div className="mt-4 bg-slate-900/60 rounded-xl border border-slate-800 p-4 text-sm text-slate-300">
                <p className="font-medium flex items-center gap-2">
                  <FiFileText /> {bulkSummary.fileName}
                </p>
                <p className="mt-2">
                  Saved: <span className="text-emerald-400">{bulkSummary.successCount || 0}</span>
                </p>
                {typeof bulkSummary.failureCount === 'number' && bulkSummary.failureCount > 0 && (
                  <p>
                    Failed: <span className="text-rose-400">{bulkSummary.failureCount}</span>
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 text-xs text-slate-500 space-y-2">
              <p>Expected format:</p>
              <pre className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 text-[11px] leading-5 overflow-x-auto">
{`[
  {
    "question": "Who wrote Les Misérables?",
    "correct_answer": "Victor Hugo",
    "incorrect_answers": ["Charles Dickens", "Mark Twain", "Jules Verne"],
    "category": "Literature",
    "difficulty": "medium",
    "type": "multiple",
    "solution": "Hugo published the novel in 1862."
  }
]`}
              </pre>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Broadcast center</h2>
              <button
                onClick={archiveOldNotifications}
                disabled={actionLoading}
                className="text-xs text-rose-300 hover:text-rose-200 disabled:opacity-60"
              >
                Archive 30d+
              </button>
            </div>
            <form className="space-y-4" onSubmit={handleSendNotification}>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Message</label>
                <textarea
                  className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-3 text-sm focus:outline-none focus:border-indigo-500 transition"
                  rows="3"
                  value={newNotification.message}
                  onChange={(e) =>
                    setNewNotification((prev) => ({ ...prev, message: e.target.value }))
                  }
                  placeholder="Share an update, launch alert, downtime notice..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Type</label>
                  <select
                    className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-2 text-sm focus:outline-none focus:border-indigo-500 transition"
                    value={newNotification.type}
                    onChange={(e) =>
                      setNewNotification((prev) => ({ ...prev, type: e.target.value }))
                    }
                  >
                    <option value="info">Info</option>
                    <option value="update">Update</option>
                    <option value="quiz">Quiz</option>
                    <option value="welcome">Welcome</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Priority</label>
                  <select
                    className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-2 text-sm focus:outline-none focus:border-indigo-500 transition"
                    value={newNotification.priority}
                    onChange={(e) =>
                      setNewNotification((prev) => ({ ...prev, priority: e.target.value }))
                    }
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Optional CTA link</label>
                <input
                  type="url"
                  value={newNotification.cta}
                  onChange={(e) =>
                    setNewNotification((prev) => ({ ...prev, cta: e.target.value }))
                  }
                  className="w-full rounded-xl bg-slate-950/40 border border-slate-800 p-2 text-sm focus:outline-none focus:border-indigo-500 transition"
                  placeholder="https://..."
                />
              </div>
              <button
                type="submit"
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 py-3 font-medium transition disabled:opacity-50"
              >
                <FiSend />
                Broadcast notification
              </button>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Recent broadcasts</h2>
              <span className="text-xs text-slate-500">
                Showing {notifications.length} latest
              </span>
            </div>
            <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
              {notifications.length === 0 && (
                <p className="text-sm text-slate-500">No notifications sent yet.</p>
              )}
              {notifications.map((item) => {
                const ts = formatFsTimestamp(item.timestamp);
                return (
                  <article
                    key={item.id}
                    className="rounded-xl border border-slate-800 p-4 bg-slate-950/50"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-xs uppercase tracking-wide text-slate-400">
                        {item.type || 'info'}
                      </span>
                      <span className="text-xs text-slate-500">
                        {ts ? ts.toLocaleString() : '—'}
                      </span>
                    </div>
                    <p className="text-sm mt-2 text-slate-100">{item.message}</p>
                    {item.cta && (
                      <a
                        href={item.cta}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-indigo-400 hover:text-indigo-300 mt-2 inline-block"
                      >
                        {item.cta}
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>
        {renderUserDrawer()}
      </div>
    </div>
  );
};

export default AdminPanel;

